import React, { useEffect, useState } from 'react';
import {
  Card,
  CardHeader,
  Text,
  Title3,
  Button,
  Spinner,
  Switch,
  Label,
  Input,
  Badge,
  Divider,
  Field,
  makeStyles,
  shorthands,
  tokens,
} from '@fluentui/react-components';
import { MessageBar, MessageBarTitle, MessageBarBody, MessageBarActions } from '../components/Common/ScrollableMessageBar';
import {
  PlayRegular,
  ClockRegular,
  CheckmarkCircle16Filled,
  DismissCircle16Regular,
  InfoRegular,
  ArrowLeftRegular,
  ArrowSync24Regular } from
'@fluentui/react-icons';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../services/apiClient';
import { useCommonSettingsStyles } from './commonSettingsStyles';

const useStyles = makeStyles({
  container: {
    ...shorthands.padding('24px'),
    maxWidth: '1200px',
    ...shorthands.margin('0', 'auto')
  },
  card: {
    ...shorthands.padding('24px'),
    marginBottom: '24px',
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.borderRadius('8px'),
    boxShadow: tokens.shadow4
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'center',
    ...shorthands.gap('12px'),
    marginBottom: '16px'
  },
  cardIcon: {
    fontSize: '24px',
    color: tokens.colorBrandForeground1
  },
  section: {
    ...shorthands.padding('20px', '0')
  },
  settingRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '16px'
  },
  settingInfo: {
    flex: 1
  },
  intervalControl: {
    display: 'flex',
    ...shorthands.gap('12px'),
    alignItems: 'center'
  },
  intervalInput: {
    width: '120px'
  },
  statusInfo: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap('12px')
  },
  statusRow: {
    display: 'flex',
    alignItems: 'center',
    ...shorthands.gap('8px')
  },
  statusValue: {
    display: 'flex',
    alignItems: 'center',
    ...shorthands.gap('4px')
  },
  successIcon: {
    color: tokens.colorPaletteGreenForeground1,
    fontSize: '16px'
  },
  errorIcon: {
    color: tokens.colorPaletteRedForeground1,
    fontSize: '16px'
  },
  infoIcon: {
    color: tokens.colorNeutralForeground3,
    fontSize: '16px'
  },
  helpText: {
    marginTop: '8px',
    color: tokens.colorNeutralForeground3
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '400px'
  },
  messageBar: {
    marginBottom: '20px'
  }
});

// MIGRATED: This interface now receives data from unified drift+sync system
// Backend maps unified system status to this legacy format for compatibility
interface TeamsSyncConfig {
  enabled: boolean;
  intervalMinutes: number;
  lastSync?: string;
  lastSyncStatus?: 'success' | 'failed';
  lastSyncMessage?: string;
  totalPoliciesSynced?: number;
  configMethod?: string; // 'environment_variable' indicates unified system
}

const StatusRow: React.FC<{
  label: string;
  children: React.ReactNode;
  className?: string;
}> = ({ label, children, className }) => {
  const styles = useStyles();
  return (
    <div className={className || styles.statusRow} data-testid="teams-sync-settings-status-row-div">
      <Text weight="semibold" data-testid="teams-sync-settings-status-label">{label}</Text>
      {children}
    </div>);

};

const SettingRow: React.FC<{
  label: string;
  description: string;
  children: React.ReactNode;
}> = ({ label, description, children }) => {
  const styles = useStyles();
  return (
    <div className={styles.settingRow} data-testid="teams-sync-settings-setting-row-div">
      <div className={styles.settingInfo} data-testid="teams-sync-settings-setting-info-div">
        <Label weight="semibold" size="large" data-testid="teams-sync-settings-setting-label">{label}</Label>
        <Text size={300} style={{ marginLeft: '6px' }} data-testid="teams-sync-settings-description">
          {description}
        </Text>
      </div>
      {children}
    </div>);

};

const TeamsSyncSettings: React.FC = () => {
  const styles = useStyles();
  const commonStyles = useCommonSettingsStyles();
  const navigate = useNavigate();
  const [syncConfig, setSyncConfig] = useState<TeamsSyncConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [message, setMessage] = useState<{type: 'success' | 'error' | 'info';text: string;} | null>(null);
  const [tempInterval, setTempInterval] = useState<string>('60');

  useEffect(() => {
    loadSyncConfig();
  }, []);

  const loadSyncConfig = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get<TeamsSyncConfig>('/teams/sync-scheduler/config');
      // Backend returns { success, data: config }, apiClient wraps in { data: { success, data: config } }
      // So response.data.data contains the actual config
      const config = (response.data as any).data || response.data;
      setSyncConfig(config);
      setTempInterval(config.intervalMinutes.toString());
    } catch (error) {
      console.error('Failed to load sync configuration:', error);
      setMessage({ type: 'error', text: 'Failed to load sync configuration' });
    } finally {
      setLoading(false);
    }
  };

  // Helper function to update sync config and eliminate duplication
  const updateSyncConfig = async (
  updatedConfig: Partial<TeamsSyncConfig>,
  successMessage: string,
  errorMessage: string,
  errorLogPrefix: string) =>
  {
    if (!syncConfig) return;

    setSaving(true);
    setMessage(null);

    try {
      await apiClient.put('/teams/sync-scheduler/config', {
        enabled: updatedConfig.enabled ?? syncConfig.enabled,
        intervalMinutes: updatedConfig.intervalMinutes ?? syncConfig.intervalMinutes
      });

      setSyncConfig({
        ...syncConfig,
        ...updatedConfig
      });

      setMessage({
        type: 'success',
        text: successMessage
      });
    } catch (error) {
      console.error(`${errorLogPrefix}:`, error);
      setMessage({ type: 'error', text: errorMessage });
    } finally {
      setSaving(false);
    }
  };

  const handleToggleSync = async () => {
    if (!syncConfig) return;

    await updateSyncConfig(
      { enabled: !syncConfig.enabled },
      `Teams sync scheduler ${!syncConfig.enabled ? 'enabled' : 'disabled'}`,
      'Failed to update sync configuration',
      'Failed to toggle sync'
    );
  };

  const handleUpdateInterval = async () => {
    const intervalNum = parseInt(tempInterval);

    if (isNaN(intervalNum) || intervalNum < 1 || intervalNum > 1440) {
      setMessage({ type: 'error', text: 'Interval must be between 1 and 1440 minutes (24 hours)' });
      return;
    }

    if (!syncConfig) return;

    await updateSyncConfig(
      { intervalMinutes: intervalNum },
      `Sync interval updated to ${intervalNum} minutes`,
      'Failed to update sync interval',
      'Failed to update interval'
    );
  };

  const handleManualSync = async () => {
    setSyncing(true);
    setMessage(null);

    try {
      const response = await apiClient.post<{
        success: boolean;
        message: string;
        data?: {
          policiesCount?: number;
        };
      }>('/teams/sync-scheduler/trigger');

      // Debug logging
      console.log('Sync trigger response:', JSON.stringify(response, null, 2));
      console.log('response.data:', response.data);
      console.log('response.data.success:', response.data.success);
      console.log('response.data.data:', response.data.data);

      // Backend returns { success, message, data: { policiesCount } }
      // apiClient returns ApiResponse<T> where T is the backend response
      // So response.data contains { success, message, data: { policiesCount } }
      if (response.data.success || (response as any).success) {
        const policyCount = response.data.data?.policiesCount;
        setMessage({
          type: 'success',
          text: `${response.data.message || 'Sync completed'}${policyCount ? ` (${policyCount} policies)` : ''}`
        });
        // Reload config to get updated last sync info
        await loadSyncConfig();
      } else {
        setMessage({ type: 'error', text: response.data.message || 'Sync failed' });
      }
    } catch (error) {
      console.error('Failed to trigger sync:', error);
      setMessage({ type: 'error', text: 'Failed to trigger manual sync' });
    } finally {
      setSyncing(false);
    }
  };

  const formatLastSync = (date?: string) => {
    if (!date) return 'Never';

    const lastSync = new Date(date);
    const now = new Date();
    const diffInMinutes = Math.floor((now.getTime() - lastSync.getTime()) / 60000);

    if (diffInMinutes < 1) {
      return 'Just now';
    } else if (diffInMinutes < 60) {
      return `${diffInMinutes} minute${diffInMinutes > 1 ? 's' : ''} ago`;
    } else if (diffInMinutes < 1440) {
      const hours = Math.floor(diffInMinutes / 60);
      return `${hours} hour${hours > 1 ? 's' : ''} ago`;
    } else {
      return lastSync.toLocaleString();
    }
  };

  const getNextSyncTime = () => {
    if (!syncConfig?.enabled || !syncConfig.lastSync) return null;

    const lastSyncTime = new Date(syncConfig.lastSync).getTime();
    const intervalMs = syncConfig.intervalMinutes * 60000;
    const nextSyncTime = lastSyncTime + intervalMs;
    const now = Date.now();
    const minutesUntilNext = Math.max(0, Math.floor((nextSyncTime - now) / 60000));

    return minutesUntilNext;
  };

  if (loading) {
    return (
      <div className={styles.loadingContainer} data-testid="teams-sync-settings-loading-container-div">
        <Spinner size="large" label="Loading sync settings..." data-testid="teams-sync-settings-loading-sync-settings-spinner" />
      </div>);

  }

  return (
    <div className={styles.container} data-testid="teams-sync-settings-container-div">
      <div className={commonStyles.header} data-testid="teams-sync-settings-header-div">
        <Text className={commonStyles.title} data-testid="teams-sync-settings-teams-policy-sync-scheduler-text">
          <ArrowSync24Regular data-testid="teams-sync-settings-arrow-sync24-regular" />
          Teams Policy Sync Scheduler
        </Text>
        <Button
          appearance="secondary"
          icon={<ArrowLeftRegular data-testid="teams-sync-settings-arrow-left-regular" />}
          onClick={() => navigate('/settings')} data-testid="teams-sync-settings-back-to-settings-button">

          Back to Settings
        </Button>
      </div>

      {message &&
      <MessageBar
        intent={message.type === 'error' ? 'error' : message.type === 'success' ? 'success' : 'info'}
        className={styles.messageBar} data-testid="teams-sync-settings-message-bar">

          <MessageBarBody data-testid="teams-sync-settings-message-bar-body">
            <MessageBarTitle data-testid="teams-sync-settings-message-bar-title">{message.text}</MessageBarTitle>
          </MessageBarBody>
          <MessageBarActions data-testid="teams-sync-settings-message-bar-actions">
            <Button
            appearance="transparent"
            size="small"
            onClick={() => setMessage(null)} data-testid="teams-sync-settings-dismiss-button">

              Dismiss
            </Button>
          </MessageBarActions>
        </MessageBar>
      }

      <Card className={styles.card} data-testid="teams-sync-settings-card">
        <CardHeader
          header={
          <div className={styles.cardHeader} data-testid="teams-sync-settings-card-header-div">
              <Title3 data-testid="teams-sync-settings-background-sync-configuration-title3">Background Sync Configuration</Title3>
            </div>
          }
          description="Automatically synchronize Teams Admin Center policies with the ADM database on a scheduled interval." data-testid="teams-sync-settings-card-header" />

        
        {syncConfig &&
        <>
            <div className={styles.section} data-testid="teams-sync-settings-section-div">
              <SettingRow
              label="Automatic Sync"
              description="Enable background synchronization to keep Teams policies up-to-date" data-testid="teams-sync-settings-automatic-sync-setting-row">

                <Switch
                checked={syncConfig.enabled}
                onChange={handleToggleSync}
                disabled={saving} data-testid="teams-sync-settings-switch" />

              </SettingRow>
            </div>

            <Divider data-testid="teams-sync-settings-sync-interval-divider" />

            <div className={styles.section} data-testid="teams-sync-settings-section-2-div">
              <SettingRow
              label="Sync Interval"
              description="How often to sync Teams policies (in minutes)" data-testid="teams-sync-settings-sync-interval-setting-row">

                <div className={styles.intervalControl} data-testid="teams-sync-settings-interval-control-div">
                  <Field data-testid="teams-sync-settings-field">
                    <Input
                    type="number"
                    value={tempInterval}
                    onChange={(e, data) => setTempInterval(data.value)}
                    min="1"
                    max="1440"
                    disabled={saving}
                    className={styles.intervalInput} data-testid="teams-sync-settings-input" />

                  </Field>
                  <Button
                  appearance="secondary"
                  onClick={handleUpdateInterval}
                  disabled={saving || tempInterval === syncConfig.intervalMinutes.toString()} data-testid="teams-sync-settings-update-button">

                    Update
                  </Button>
                </div>
              </SettingRow>
            </div>

            <Divider data-testid="teams-sync-settings-sync-status-divider" />

            <div className={styles.section} data-testid="teams-sync-settings-section-3-div">
              <Title3 style={{ marginBottom: '16px' }} data-testid="teams-sync-settings-sync-status-title3">Sync Status</Title3>
              <div className={styles.statusInfo} data-testid="teams-sync-settings-status-info-div">
                <StatusRow label="Status:" data-testid="teams-sync-settings-status-status-row">
                  <Badge
                  appearance="filled"
                  color={syncConfig.enabled ? 'success' : 'warning'} data-testid="teams-sync-settings-status-badge">

                    {syncConfig.enabled ? 'Enabled' : 'Disabled'}
                  </Badge>
                </StatusRow>
                
                <StatusRow label="Last Sync:" data-testid="teams-sync-settings-last-sync-status-row">
                  <div className={styles.statusValue} data-testid="teams-sync-settings-status-value-div">
                    {syncConfig.lastSyncStatus === 'success' ?
                  <CheckmarkCircle16Filled className={styles.successIcon} data-testid="teams-sync-settings-checkmark-circle16-filled" /> :
                  syncConfig.lastSyncStatus === 'failed' ?
                  <DismissCircle16Regular className={styles.errorIcon} data-testid="teams-sync-settings-dismiss-circle16-regular" /> :

                  <InfoRegular className={styles.infoIcon} data-testid="teams-sync-settings-info-regular" />
                  }
                    <Text data-testid="teams-sync-settings-last-sync">{formatLastSync(syncConfig.lastSync)}</Text>
                  </div>
                </StatusRow>
                
                {syncConfig.lastSyncMessage &&
              <StatusRow label="Last Result:" data-testid="teams-sync-settings-last-result-status-row">
                    <Text size={300} data-testid="teams-sync-settings-last-result">{syncConfig.lastSyncMessage}</Text>
                  </StatusRow>
              }
                
                {syncConfig.totalPoliciesSynced !== undefined &&
              <StatusRow label="Policies Synced:" data-testid="teams-sync-settings-policies-synced-status-row">
                    <Badge appearance="tint" color="informative" data-testid="teams-sync-settings-policies-synced-badge">
                      {syncConfig.totalPoliciesSynced}
                    </Badge>
                  </StatusRow>
              }
                
                {syncConfig.enabled && getNextSyncTime() !== null &&
              <StatusRow label="" data-testid="teams-sync-settings-status-row">
                    <ClockRegular className={styles.infoIcon} data-testid="teams-sync-settings-clock-regular" />
                    <Text size={300} data-testid="teams-sync-settings-next-sync-in-approximately-text">
                      Next sync in approximately {getNextSyncTime()} minutes
                    </Text>
                  </StatusRow>
              }
              </div>
            </div>

            <Divider data-testid="teams-sync-settings-manual-sync-divider" />

            <div className={styles.section} data-testid="teams-sync-settings-section-4-div">
              <Button
              appearance="primary"
              icon={<PlayRegular data-testid="teams-sync-settings-play-regular" />}
              onClick={handleManualSync}
              disabled={syncing} data-testid="teams-sync-settings-button">

                {syncing ? 'Syncing...' : 'Sync Now'}
              </Button>
              <Text size={200} className={styles.helpText} style={{ marginLeft: '6px' }} data-testid="teams-sync-settings-manually-sync-all-teams-admin-center-text">
                Manually sync all Teams Admin Center policies with the ADM database.
              </Text>
            </div>
          </>
        }
      </Card>
    </div>);

};

export default TeamsSyncSettings;